import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "qa1st-cookie-consent";
const GTM_ID = "GTM-K8N5BPBN";

type ConsentValue = "accepted" | "rejected";

let gtmLoaded = false;

export const loadGtm = () => {
  if (gtmLoaded || typeof window === "undefined") return;
  gtmLoaded = true;
  const w = window as unknown as Record<string, unknown[]>;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

export const getConsent = (): ConsentValue | null => {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "accepted" || stored === "rejected" ? stored : null;
};

export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent("qa1st:open-cookie-settings"));
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === "accepted") {
      loadGtm();
    } else if (consent === null) {
      setVisible(true);
    }

    const reopen = () => setVisible(true);
    window.addEventListener("qa1st:open-cookie-settings", reopen);
    return () => window.removeEventListener("qa1st:open-cookie-settings", reopen);
  }, []);

  const decide = (value: ConsentValue) => {
    window.localStorage.setItem(STORAGE_KEY, value);
    if (value === "accepted") loadGtm();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur px-4 py-4 md:px-8"
    >
      <div className="container-narrow flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground max-w-2xl">
          We use essential cookies to run this site. With your permission we also use analytics cookies
          (Google Analytics via Google Tag Manager) to understand how the site is used. You can change your
          choice at any time. Read our{" "}
          <Link to="/cookies" className="underline hover:text-foreground">cookie policy</Link> and{" "}
          <Link to="/privacy" className="underline hover:text-foreground">privacy policy</Link>.
        </p>
        <div className="flex gap-3 shrink-0">
          <Button variant="outline" onClick={() => decide("rejected")}>
            Reject analytics
          </Button>
          <Button onClick={() => decide("accepted")}>Accept analytics</Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
